import { motion } from "framer-motion";
import { FileCheck, Users, Heart, Baby, CheckCircle, AlertCircle, ChevronRight } from "lucide-react";
import { useState } from "react";

const eligibleCategories = [
  {
    icon: Heart,
    title: "Инвалиды I или II группы",
    description: "Граждане РБ с установленной инвалидностью первой или второй группы",
  },
  {
    icon: Users,
    title: "Многодетные семьи",
    description: "Родители, воспитывающие троих и более несовершеннолетних детей",
  },
  {
    icon: Baby,
    title: "Опекуны детей-инвалидов",
    description: "Законные представители детей-инвалидов в возрасте до 18 лет",
  },
];

const conditions = [
  "Один автомобиль в год с даты предыдущего ввоза с льготой",
  "Автомобиль ввезён после 13 апреля 2019 года",
  "Утилизационный сбор оплачивается полностью",
  "Необходимы подтверждающие документы статуса",
  "Оформление на имя льготника или по нотариальной доверенности",
];

const eligibilityQuestions = [
  {
    id: "status",
    question: "Выберите ваш статус:",
    options: [
      { value: "invalid1", label: "Инвалид I группы", eligible: true },
      { value: "invalid2", label: "Инвалид II группы", eligible: true },
      { value: "invalid3", label: "Инвалид III группы", eligible: false },
      { value: "parent_multi", label: "Родитель в многодетной семье (3+ детей)", eligible: true },
      { value: "parent_invalid_child", label: "Родитель/опекун ребёнка-инвалида до 18 лет", eligible: true },
      { value: "none", label: "Ни один из вышеперечисленных", eligible: false },
    ]
  },
  {
    id: "residence",
    question: "Вы постоянно проживаете в Республике Беларусь?",
    options: [
      { value: "yes", label: "Да", eligible: true },
      { value: "no", label: "Нет", eligible: false },
    ]
  },
  {
    id: "previous_use",
    question: "Использовали ли вы льготу по Указу №140 за последний год?",
    options: [
      { value: "no", label: "Нет, не использовал(а)", eligible: true },
      { value: "yes", label: "Да, использовал(а)", eligible: false },
    ]
  },
];

export default function Decree140Section() {
  const [checkStep, setCheckStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showChecker, setShowChecker] = useState(false);
  const [checkResult, setCheckResult] = useState(null);

  const scrollToContact = () => {
    document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
  };

  const handleAnswer = (questionId, value, eligible) => {
    const newAnswers = { ...answers, [questionId]: { value, eligible } };
    setAnswers(newAnswers);
    
    if (!eligible) {
      // Не имеет права на льготу
      setCheckResult({
        eligible: false,
        reason: questionId === "status" 
          ? "К сожалению, ваш статус не подходит под условия Указа №140"
          : questionId === "residence"
          ? "Льгота предоставляется только постоянно проживающим в РБ"
          : "Льготу можно использовать только один раз в год"
      });
    } else if (checkStep < eligibilityQuestions.length - 1) {
      setCheckStep(checkStep + 1);
    } else {
      // Все ответы положительные
      setCheckResult({
        eligible: true,
        reason: "Поздравляем! Вы имеете право на льготу 50% по Указу №140"
      });
    }
  };

  const resetChecker = () => {
    setCheckStep(0);
    setAnswers({});
    setCheckResult(null);
  };

  return (
    <section 
      id="decree-140" 
      className="py-20 md:py-24 bg-[#050505]"
      data-testid="decree-140-section"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
            <FileCheck className="w-4 h-4" />
            Указ №140
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-4">
            Льготная растаможка <span className="text-green-600">−50%</span>
          </h2>
          <p className="text-base md:text-lg text-white/50 max-w-2xl mx-auto">
            Указ Президента Республики Беларусь №140 от 10.04.2019 предоставляет право 
            на возмещение 50% таможенных пошлин и налогов определённым категориям граждан
          </p>
        </motion.div>

        {/* Eligible Categories */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1, duration: 0.5 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12"
        >
          {eligibleCategories.map((category, index) => (
            <div 
              key={category.title}
              className="bg-[#0F0F0F] rounded-2xl p-6 border border-green-100 hover:border-green-300 hover:shadow-lg hover:shadow-green-100/50 transition-all"
              data-testid={`category-card-${index}`}
            >
              <div className="w-14 h-14 bg-green-100 rounded-2xl flex items-center justify-center mb-4">
                <category.icon className="w-7 h-7 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">
                {category.title}
              </h3>
              <p className="text-sm text-white/50">
                {category.description}
              </p>
            </div>
          ))}
        </motion.div>

        {/* Conditions & Benefits */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Conditions */}
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="bg-[#0F0F0F] rounded-2xl p-6 md:p-8 border border-white/10"
          >
            <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-[#F97316]" />
              Условия получения льготы
            </h3>
            <ul className="space-y-4">
              {conditions.map((condition, index) => (
                <li key={index} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-white/50">{condition}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Benefits Example */}
          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="bg-gradient-to-br from-green-600 to-green-700 rounded-2xl p-6 md:p-8 text-white"
          >
            <h3 className="text-xl font-bold mb-6">
              Пример экономии
            </h3>
            <div className="space-y-4 mb-6">
              <div className="bg-[#0F0F0F]/10 rounded-sm p-4">
                <p className="text-sm text-white/70 mb-1">Автомобиль</p>
                <p className="font-semibold">Toyota Camry 2.5L, 2023 г.в., $35 000</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-[#0F0F0F]/10 rounded-sm p-4">
                  <p className="text-sm text-white/70 mb-1">Без льготы</p>
                  <p className="text-2xl font-bold">~$16 800</p>
                  <p className="text-xs text-white/60">таможенные платежи</p>
                </div>
                <div className="bg-[#0F0F0F]/20 rounded-sm p-4 ring-2 ring-white/30">
                  <p className="text-sm text-white/70 mb-1">По Указу №140</p>
                  <p className="text-2xl font-bold">~$8 400</p>
                  <p className="text-xs text-white/60">экономия 50%</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 text-white/80 text-sm">
              <AlertCircle className="w-4 h-4" />
              <span>Утильсбор оплачивается полностью</span>
            </div>
          </motion.div>
        </div>

        {/* Eligibility Checker */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.35, duration: 0.5 }}
          className="mt-12 bg-[#0F0F0F] rounded-none p-6 md:p-8 border border-white/10 shadow-lg"
          data-testid="eligibility-checker"
        >
          <div className="text-center mb-6">
            <h3 className="text-xl md:text-2xl font-bold text-white mb-2">
              Проверьте право на льготу
            </h3>
            <p className="text-white/50">
              Ответьте на несколько вопросов, чтобы узнать, можете ли вы воспользоваться льготой
            </p>
          </div>

          {!showChecker ? (
            <div className="text-center">
              <button
                onClick={() => setShowChecker(true)}
                className="inline-flex items-center gap-2 bg-white hover:bg-gray-200 text-black px-8 py-4 rounded-full font-semibold transition-colors"
                data-testid="start-checker-btn"
              >
                Начать проверку
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          ) : checkResult ? (
            <div className="text-center">
              <div className={`w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-4 ${
                checkResult.eligible ? "bg-green-100" : "bg-red-100"
              }`}>
                {checkResult.eligible ? (
                  <CheckCircle className="w-10 h-10 text-green-600" />
                ) : (
                  <AlertCircle className="w-10 h-10 text-red-600" />
                )}
              </div>
              <h4 className={`text-xl font-bold mb-2 ${
                checkResult.eligible ? "text-green-600" : "text-red-600"
              }`}>
                {checkResult.eligible ? "Вы имеете право на льготу!" : "Льгота недоступна"}
              </h4>
              <p className="text-white/50 mb-6 max-w-md mx-auto">
                {checkResult.reason}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                {checkResult.eligible && (
                  <button
                    onClick={scrollToContact}
                    className="inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-full font-semibold transition-colors"
                  >
                    Оформить со льготой
                  </button>
                )}
                <button
                  onClick={resetChecker}
                  className="inline-flex items-center justify-center gap-2 bg-[#0A0A0A] hover:bg-[#1A1A1A] text-white px-6 py-3 rounded-full font-semibold transition-colors border border-white/20"
                >
                  Пройти заново
                </button>
              </div>
            </div>
          ) : (
            <div className="max-w-lg mx-auto">
              {/* Progress */}
              <div className="flex items-center gap-2 mb-6">
                {eligibilityQuestions.map((_, index) => (
                  <div 
                    key={index}
                    className={`flex-1 h-2 rounded-full transition-colors ${
                      index < checkStep ? "bg-green-500" : index === checkStep ? "bg-white" : "bg-[#333]"
                    }`}
                  />
                ))}
              </div>
              
              {/* Current Question */}
              <div className="mb-6">
                <p className="text-sm text-white/50 mb-2">
                  Вопрос {checkStep + 1} из {eligibilityQuestions.length}
                </p>
                <h4 className="text-lg font-semibold text-white mb-4">
                  {eligibilityQuestions[checkStep].question}
                </h4>
                <div className="space-y-3">
                  {eligibilityQuestions[checkStep].options.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => handleAnswer(eligibilityQuestions[checkStep].id, option.value, option.eligible)}
                      className="w-full text-left p-4 rounded-sm border-2 border-white/10 hover:border-[#2563EB] hover:bg-[#0A0A0A] transition-all"
                      data-testid={`option-${option.value}`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
              
              {checkStep > 0 && (
                <button
                  onClick={() => setCheckStep(checkStep - 1)}
                  className="text-white/50 hover:text-white text-sm font-medium"
                >
                  ← Назад
                </button>
              )}
            </div>
          )}
        </motion.div>

        {/* CTA */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4, duration: 0.5 }}
          className="mt-12 text-center"
        >
          <p className="text-white/50 mb-4">
            Поможем оформить льготную растаможку по Указу №140
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={scrollToContact}
              className="inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-full font-semibold transition-colors"
              data-testid="decree-cta-btn"
            >
              Узнать подробнее
            </button>
            <a
              href="https://pravo.by/document/?guid=12551&p0=P31900140"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-[#0F0F0F] border-2 border-white/10 hover:border-green-500 text-white px-8 py-4 rounded-full font-semibold transition-colors"
              data-testid="decree-official-link"
            >
              <FileCheck className="w-5 h-5" />
              Текст Указа на pravo.by
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
